/**
 * "A new version of plane is available", shown only when there is one to take.
 *
 * Created on demand by `src/main.js`, which owns the service worker registration: most sessions
 * never see a deploy while they are open. The activation itself (posting to the waiting worker,
 * reloading once on `controllerchange`) lives there too; this component only asks.
 *
 * @fires update-prompt/accepted
 */
import { LitElement, html } from 'lit';

import { formStyles } from '../../styles/shared/forms.js';
import { planeUpdatePromptStyles } from '../../styles/components/plane-update-prompt.js';

class PlaneUpdatePrompt extends LitElement {
  static properties = {
    visible: { state: true },
  };

  static styles = [
    formStyles,
    planeUpdatePromptStyles,
  ];

  constructor() {
    super();
    this.visible = false;
    // Publishes the height of the bar as --bottom-overlay (base.css) while it shows.
    this.resizeObserver = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty('--bottom-overlay', `${entry.borderBoxSize[0].blockSize}px`);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver.disconnect();
    document.documentElement.style.removeProperty('--bottom-overlay');
  }

  updated() {
    this.resizeObserver.disconnect();
    const bar = this.renderRoot.querySelector('.bar');
    if (bar) {
      this.resizeObserver.observe(bar);
    } else {
      document.documentElement.style.removeProperty('--bottom-overlay');
    }
  }

  /** Called by main.js once a waiting worker is an update, never on the first install. */
  show() {
    this.visible = true;
  }

  accept() {
    this.visible = false;
    this.dispatchEvent(new CustomEvent('update-prompt/accepted', { bubbles: true }));
  }

  render() {
    this.hidden = !this.visible;
    if (!this.visible) {
      return null;
    }
    return html`
      <div class="bar" role="status">
        <span>A new version of plane is available.</span>
        <button type="button" class="small primary" @click=${this.accept}>Update</button>
      </div>
    `;
  }
}

customElements.define('plane-update-prompt', PlaneUpdatePrompt);
